import React, { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

function buildScramble(text, settled = 0) {
  return text
    .split("")
    .map((char, index) => {
      if (char === " ") return " ";
      if (index < settled) return text[index];
      return CHARS[(Math.random() * CHARS.length) | 0];
    })
    .join("");
}

export default function ScrambleText({
  text,
  as: Component = "span",
  className,
  duration = 560,
  delayMs = 0,
}) {
  const prefersReducedMotion = useReducedMotion();
  const [display, setDisplay] = useState(() =>
    prefersReducedMotion || !text ? text : buildScramble(text)
  );
  const timerRef = useRef(null);
  const delayRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion || !text) {
      setDisplay(text);
      return undefined;
    }

    clearInterval(timerRef.current);
    clearTimeout(delayRef.current);
    setDisplay(buildScramble(text));

    delayRef.current = window.setTimeout(() => {
      let frame = 0;
      const totalFrames = Math.max(8, Math.round(duration / 32));

      timerRef.current = window.setInterval(() => {
        frame += 1;
        const progress = frame / totalFrames;
        const settled = Math.floor(text.length * progress);
        const next = progress >= 1 ? text : buildScramble(text, settled);

        setDisplay(next);

        if (progress >= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }, 32);
    }, delayMs);

    return () => {
      clearInterval(timerRef.current);
      clearTimeout(delayRef.current);
    };
  }, [delayMs, duration, prefersReducedMotion, text]);

  return (
    <Component className={className}>
      <span
        aria-hidden="true"
        style={{
          position: "relative",
          display: "inline-grid",
          alignItems: "start",
          whiteSpace: "inherit",
        }}
      >
        <span style={{ visibility: "hidden", whiteSpace: "inherit" }}>{text}</span>
        <span
          style={{
            position: "absolute",
            inset: 0,
            whiteSpace: "inherit",
          }}
        >
          {display}
        </span>
      </span>
      <span
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {text}
      </span>
    </Component>
  );
}
